<template>
  <div>
    <fieldset
      :disabled="readonly"
      style="border:0;padding:0;margin:0;min-width:0;"
    >
      <div v-if="!loadingIncident">
        <form-navigation-tabs
          :tabs="tabs"
          :active="selectedTab"
          v-on:selected="setSelectedTab"
        ></form-navigation-tabs>

        <div v-show="selectedTab === 'details'">
          <form>
            <div class="card-body">
              <div v-if="hireLoaded">
                <hire-details
                  :details="details"
                  :vehicleForm="vehicleForm"
                  @changed="changeHandler"
                ></hire-details>

                <hire-drivers
                  :hire="hire"
                  :incident="incident"
                  :client="client"
                  @changed="changeHandler"
                ></hire-drivers>

                <fieldset>
                  <legend>Vehicle</legend>

                  <hire-vehicle
                    :hire="hire"
                    :incident="incident"
                    :vehicleForm="vehicleForm"
                    @changed="changeHandler"
                    @vehicleSelected="setFleetVehicle"
                  ></hire-vehicle>
                </fieldset>

                <fieldset>
                  <legend>Rates</legend>

                  <hire-rates
                    v-if="hiresLoaded"
                    :details-form="details"
                    :vehicle-form="vehicleForm"
                    :rates-form="ratesForm"
                    :hire="hire"
                    :hires="hires"
                    :selected-tariff="selectedTariff"
                    :requires-non-standard="requiresNonStandardCharge"
                    @changed="changeHandler"
                  ></hire-rates>
                </fieldset>
              </div>
              <div v-else>
                <preloader></preloader>
              </div>
            </div>

            <div class="card-footer text-right">
              <form-button
                :indicator="requestHdIndicator"
                level="warning"
                :disabled="disableHdRequest || hdRequested"
                v-on:clicked="requestHireDelivery"
                v-if="hasPermission('hires.update')"
              >
                {{
                  hdRequested
                    ? 'Hire Delivery Requested'
                    : 'Request Hire Delivery'
                }}
              </form-button>
              <form-button
                :indicator="indicator"
                :disabled="generating"
                v-on:clicked="submit"
                v-if="hasPermission('hires.update')"
              >
                Save
              </form-button>
            </div>
          </form>
        </div>

        <hire-vehicle-agreement
          :hire="hire"
          :incident="incident"
          v-if="selectedTab === 'hire_agreement'"
        ></hire-vehicle-agreement>

        <hire-mitigation-statement
          :hire="hire"
          :incident="incident"
          v-show="selectedTab === 'mitigation_statement'"
        ></hire-mitigation-statement>
      </div>
    </fieldset>
  </div>
</template>
<script>
import HireDetails from './Children/HireDetails';
import HireVehicle from './Children/HireVehicle';
import HireDrivers from './Children/HireDrivers';
import HireRates from './Children/HireRates';
import HireMitigationStatement from './Children/HireMitigationStatement';
import HireVehicleAgreement from './Children/HireVehicleAgreement';

export default {
  props: {
    incident: {
      type: String,
      required: false
    },

    hire: {
      type: Object,
      required: false
    },

    hideButton: {
      type: Boolean,
      default: false
    },

    modalView: {
      type: Boolean,
      default: false
    },

    readonly: {
      type: Boolean,
      default: false
    }
  },

  components: {
    HireDetails,
    HireVehicle,
    HireDrivers,
    HireRates,
    HireVehicleAgreement,
    HireMitigationStatement
  },

  computed: {
    address() {
      return {
        address_line_one: this.details ? this.details.address_line_one : null,
        address_line_two: this.details ? this.details.address_line_two : null,
        city: this.details ? this.details.city : null,
        postcode: this.details ? this.details.postcode : null
      };
    },

    client() {
      return this.$store.getters['Incident/client'];
    },

    hasVehicle() {
      return Object.keys(this.vehicleData).length > 0;
    },

    groups() {
      return this.$store.getters['VehicleGroups/all'];
    },

    makes() {
      return this.$store.getters['VehicleMakes/all'];
    },

    transmissions() {
      return this.$store.getters['VehicleTransmissions/all'];
    },

    isGTA() {
      return this.$store.getters['Incident/isGTA'];
    },

    useVehicleFromFleet() {
      return this.vehicleForm.use_vehicle_from_fleet;
    },

    vehicleGroup() {
      return this.vehicleForm.group_id;
    },

    /**
     * The non-standard driver charge is required if:
     *   - The client is on a CBT
     *   - The client does not hold a full licence
     *   - Any rider is under 25
     *   - Any rider is over 70
     *   - Any rider has held their licence for less than 12 months
     *
     * @returns {boolean}
     */
    requiresNonStandardCharge() {
      if (this.clientOnCBT) {
        return true;
      }

      if (this.clientDoesNotHaveFullLicence) {
        return true;
      }

      if (this.ridersUnder25) {
        return true;
      }

      if (this.ridersOver70) {
        return true;
      }

      if (this.ridersLicenceLessThan12MonthsOld) {
        return true;
      }

      return false;
    },

    clientOnCBT() {
      return this.client?.indemnity?.have_cbt;
    },

    clientDoesNotHaveFullLicence() {
      const licenceType = this.client?.indemnity?.license_type;

      if (licenceType) {
        return !licenceType.includes('Full Bike');
      }

      return false;
    },

    ridersUnder25() {
      return this.checkRiders(rider => {
        return (
          rider.date_of_birth &&
          window.DateManager.differenceInYears(
            new Date(),
            rider.date_of_birth
          ) <= 25
        );
      });
    },

    ridersOver70() {
      return this.checkRiders(rider => {
        return (
          rider.date_of_birth &&
          window.DateManager.differenceInYears(
            new Date(),
            rider.date_of_birth
          ) >= 70
        );
      });
    },

    ridersLicenceLessThan12MonthsOld() {
      return this.checkRiders(rider => {
        return !!rider.has_new_licence;
      });
    },

    loadingIncident() {
      return this.$store.getters['Incident/loading'];
    },

    jobTypes() {
      return this.$store.getters['JobTypes/all'];
    }
  },

  data() {
    return {
      tenantIdentifier: null,
      fleetVehicles: null,
      indicator: false,
      requestHdIndicator: false,
      disableHdRequest: true,
      hdRequested: false,
      generating: false,
      searching: false,
      hireLoaded: false,
      selectedTab: 'details',
      tabs: [
        {
          name: 'details',
          label: 'Details',
          disabled: false
        },
        {
          name: 'hire_agreement',
          label: 'Hire Agreement',
          disabled: false
        },
        {
          name: 'mitigation_statement',
          label: 'Mitigation Statement',
          disabled: false
        }
      ],
      mitigation: new window.Form({
        questions: []
      }),
      vehicle: null,
      fleet_vehicle: null,
      vehicleData: {},
      editingDriver: null,
      details: new window.Form({
        address_line_one: null,
        address_line_two: null,
        city: null,
        postcode: null,
        confirmed: false,
        hire_reason: null,
        is_gta: false
      }),
      vehicleForm: new window.Form({
        hire_site_id: null,
        use_vehicle_from_fleet: true,
        fleet_vehicle_identifier: null,
        requested_start_date: null,
        start_date: null,
        requested_end_date: null,
        end_date: null,
        last_chargeable_date: null,
        group_id: null,
        registration: null,
        make_id: null,
        model: null,
        colour: null,
        transmission_type_id: null,
        fuel_type_id: null,
        start_damage: {
          mb_rear_main_body: 0,
          mb_rear_right_handlebar: 0,
          mb_rear_right_rear_view_mirror: 0,
          mb_rear_left_handlebar: 0,
          mb_rear_left_rear_view_mirror: 0,
          mb_rear_front_right_indicator: 0,
          mb_rear_front_left_indicator: 0,
          mb_rear_fuel: 0,
          mb_rear_seat: 0,
          mb_rear_back_wheel: 0,
          mb_rear_right_indicator: 0,
          mb_rear_left_indicator: 0,
          mb_rear_brake_light: 0,
          mb_left_seat: 0,
          mb_left_rear_wheel: 0,
          mb_left_rear_break_light: 0,
          mb_left_rear_right_indicator: 0,
          mb_left_main_body_engine: 0,
          mb_left_exhaust: 0,
          mb_left_rear_view_mirror: 0,
          mb_left_handlebar: 0,
          mb_left_front_wheel: 0,
          mb_left_indicator_light: 0,
          mb_left_front_head_light: 0,
          mb_right_seat: 0,
          mb_right_rear_wheel: 0,
          mb_right_rear_break_light: 0,
          mb_right_rear_right_indicator: 0,
          mb_right_main_body_engine: 0,
          mb_right_exhaust: 0,
          mb_right_rear_view_mirror: 0,
          mb_right_handlebar: 0,
          mb_right_front_wheel: 0,
          mb_right_indicator_light: 0,
          mb_right_front_head_light: 0,
          mb_front_head_lamp: 0,
          mb_front_left_handlebar: 0,
          mb_front_left_indicator: 0,
          mb_front_left_rear_view_mirror: 0,
          mb_front_right_handlebar: 0,
          mb_front_right_rear_view_mirror: 0,
          mb_front_main_body: 0,
          mb_front_right_indicator: 0,
          mb_front_wheel: 0,
          description: null
        }
      }),
      ratesForm: new window.Form({
        rates: []
      }),
      has_document: false,
      data_entered: false,
      party: {},
      hires: [],
      hiresLoaded: false,
      selectedTariff: {}
    };
  },

  mounted() {
    this.mount();
    this.getIncidentTariff();

    // Log that the user has viewed the hire
    window.Ajax.post(`/incidents/${this.incident}/log`, {
      module: 'Deployment',
      tab: 'Services - Hire - Add Hire'
    });
  },

  methods: {
    setVrm(vrm) {
      this.vehicleForm.registration = vrm;
    },

    generateDocument() {
      if (this.drivers.length === 0) {
        window.toastify.error(
          'You need to create at least one driver before creating the vehicle hire agreement!',
          'Error'
        );
      } else {
        this.complete();
      }
    },

    complete() {
      this.indicator = true;
      let target = window.open();

      this.details
        .post(`/hires/${this.hire.identifier}/details`)
        .then(response => {
          this.indicator = false;
          let hasVehicle = this.vehicle;
          let url = hasVehicle
            ? `/hires/${this.hire.identifier}/vehicles/${this.vehicle}`
            : `/hires/${this.hire.identifier}/vehicles`;
          let method = hasVehicle ? 'put' : 'post';
          this.vehicleForm[method](url)
            .then(response => {
              this.vehicle = response.identifier;

              this.rates
                .post(
                  `/hires/${this.hire.identifier}/vehicles/${this.vehicle}/rates`
                )
                .then(response => {
                  // reset monitoring of form changes
                  this.data_entered = false;
                  this.$emit('unsaved-data', this.data_entered);

                  this.generating = true;

                  window.Ajax.post(`/hires/${this.hire.identifier}/complete`)
                    .then(response => {
                      this.generating = false;
                      if (
                        this.$store.getters['user'].tenant
                          .generate_vehicle_hire_agreement
                      ) {
                        target.location.href = response.data.file;
                      }
                    })
                    .catch(error => {
                      if (
                        this.$store.getters['user'].tenant
                          .generate_vehicle_hire_agreement
                      ) {
                        target.close();
                      }
                      this.generating = false;
                      window.toastify.error(
                        'Error',
                        "Sorry, we couldn't generate the document. Please try again"
                      );
                    });
                })
                .catch(error => {
                  target.close();
                  window.toastify.error(
                    'Error',
                    'Sorry, there was an error saving the data'
                  );
                });
            })
            .catch(error => {
              target.close();
              window.toastify.error(
                'Error',
                'Sorry, there was an error saving the data'
              );
            });
        })
        .catch(error => {
          target.close();
          this.indicator = false;
          window.toastify.error(
            'Error',
            'Sorry, there was an error saving the data'
          );
        });
    },

    setAddress(address) {
      this.details.address_line_one = address.address_line_one;
      this.details.address_line_two = address.address_line_two;
      this.details.city = address.city;
      this.details.postcode = address.postcode;
    },

    isQuestionChecked(question) {
      if (this.mitigation.hasData(question.identifier)) {
        return this.mitigation[question.identifier];
      }

      return false;
    },

    mount() {
      this.$store.commit('Tariffs/retrieve');
      this.$store.commit('DriverTypes/retrieve');
      this.$store.commit('CreditHireCharges/retrieve');
      this.$store.commit('CreditHireChargeTypes/retrieve');
      this.$store.commit('JobTypes/retrieve');

      if (!Object.keys(this.client).length) {
        this.$store.dispatch('Incident/retrieve', this.incident);
      }

      window.Ajax.get('/authenticate/user')
        .then(response => {
          this.tenantIdentifier = response.data.tenant.identifier;
        })
        .catch(error => {});

      this.details.is_gta = this.isGTA;

      if (this.hire?.identifier) {
        this.hireLoaded = true;

        if (this.hire.details) {
          this.details.address_line_one = this.hire.details.address_line_one;
          this.details.address_line_two = this.hire.details.address_line_two;
          this.details.city = this.hire.details.city;
          this.details.postcode = this.hire.details.postcode;
          this.details.hire_reason = this.hire.details.hire_reason;
          this.details.confirmed = this.hire.details.confirmed;
          this.details.is_gta = this.hire.details.is_gta;
        }

        if (this.hire.vehicle && this.hire.vehicle.rates) {
          this.vehicleForm.use_vehicle_from_fleet =
            this.hire.vehicle.use_vehicle_from_fleet ?? true;
          this.vehicleForm.hire_site_id = this.hire.vehicle.hire_site_id;
          this.vehicleForm.make_id = this.hire.vehicle.make_id;
          this.vehicleForm.model = this.hire.vehicle.model;
          this.vehicleForm.colour = this.hire.vehicle.colour;
          this.vehicleForm.group_id = this.hire.vehicle.group_id;
          this.vehicleForm.transmission_type_id = this.hire.vehicle.transmission_type_id;
          this.vehicleForm.fuel_type_id = this.hire.vehicle.fuel_type_id;
          this.vehicleForm.requested_start_date = this.hire.vehicle.requested_start_date;
          this.vehicleForm.start_date = this.hire.vehicle.start_date;
          this.vehicleForm.requested_end_date = this.hire.vehicle.requested_end_date;
          this.vehicleForm.end_date = this.hire.vehicle.end_date;
          this.vehicleForm.last_chargeable_date = this.hire.vehicle.last_chargeable_date;
          this.vehicleForm.registration = this.hire.vehicle.registration;
          this.vehicleForm.start_damage = this.hire.vehicle.start_damage;

          if (this.hire.fleet_vehicle) {
            this.fleet_vehicle = this.hire.fleet_vehicle;
          }
        }
      }

      this.enableHireDeliveryRequest();
      this.disableHireDeliveryRequest();

      window.Ajax.get(`/incidents/${this.incident}/parties`).then(response => {
        const clients = response.data.filter(
          party => party.type.name === 'client'
        );

        if (clients.length) {
          this.party = clients[0];

          window.Ajax.get(
            `/incidents/${this.incident}/parties/${this.party.identifier}/hires`
          ).then(response => {
            this.hires = response.data;
            this.hiresLoaded = true;
          });
        }
      });
    },

    changeHandler() {
      if (!this.data_entered) {
        this.data_entered = true;
      }
      this.$emit('unsaved-data', this.data_entered);
    },

    submit() {
      return new Promise((resolve, reject) => {
        if (!this.details.hire_reason) {
          window.toastify.error(
            'You need to provide a reason for the hire!',
            'Error'
          );
          return false;
        }
        this.indicator = true;

        this.details
          .post(`/hires/${this.hire.identifier}/details`)
          .then(response => {
            this.indicator = false;

            const vehicleId = this.vehicle || this.hire?.vehicle?.identifier;
            let url = vehicleId
              ? `/hires/${this.hire.identifier}/vehicles/${vehicleId}`
              : `/hires/${this.hire.identifier}/vehicles`;

            this.processVehicle(vehicleId ? 'put' : 'post', url);

            // reset monitoring of form changes
            this.data_entered = false;

            this.$emit('unsaved-data', this.data_entered);

            resolve();
          })
          .catch(error => {
            this.indicator = false;
            window.toastify.error(
              'Error',
              'Sorry, there was an error saving the data'
            );
            reject();
          });
      });
    },

    processVehicle(method, url) {
      if (this.fleet_vehicle) {
        this.vehicleForm.fleet_vehicle_identifier = this.fleet_vehicle.identifier;
      }

      this.vehicleForm[method](url)
        .then(response => {
          this.vehicle = response.identifier;

          this.processRates();
        })
        .catch(error => {
          console.error(error);
          window.toastify.error(
            'Error',
            'Sorry, there was an error saving the data'
          );
        });
    },

    processRates() {
      this.ratesForm
        .post(`/hires/${this.hire.identifier}/vehicles/${this.vehicle}/rates`)
        .then(response => {
          window.toastify.success('Saved', "We've saved those details for you");
        })
        .catch(error => {
          console.error(error);
          window.toastify.error(
            'Error',
            'Sorry, there was an error saving the data'
          );
        });
    },

    viewDocument() {
      this.$router.push('/first-notice-of-loss/' + this.incident + '/complete');
    },

    updateVehicleDamage(data) {
      this.vehicleForm.start_damage = data;
    },

    setRegistration(registration) {
      this.vehicleForm.registration = registration;
    },

    setVehicle(vehicle) {
      this.vehicleForm.transmission_type_id = vehicle.transmission_type_id;
      this.vehicleForm.group_id = vehicle.group_id;
      this.vehicleForm.make_id = vehicle.make_id;
      this.vehicleForm.model = vehicle.model;
      this.vehicleForm.colour = vehicle.colour;
    },

    setGTARate(group) {
      if (group.rate) {
        this.dailyRate = this.penceToPounds(group.rate);
      }
    },

    setSelectedTab(tab) {
      this.selectedTab = tab;
    },

    setFleetVehicle(vehicle) {
      this.fleet_vehicle = vehicle;
    },

    requestHireDelivery() {
      this.requestHdIndicator = true;

      let data = {
        hire_id: this.hire.id,
        type_id: this.getJobTypeByName('hire_delivery')?.id
      };

      window.Ajax.post(`/jobs/request`, data)
        .then(response => {
          this.requestHdIndicator = false;
          this.hdRequested = true;
        })
        .catch(error => {
          this.requestHdIndicator = false;
          window.toastify.error(
            'Error',
            'Sorry, there was an issue creating the job'
          );
        });
    },

    getJobTypeByName(name) {
      return this.jobTypes.find(type => type.name === name);
    },

    enableHireDeliveryRequest() {
      this.disableHdRequest = true;
      if (this.hire?.drivers?.length > 0) {
        this.disableHdRequest = false;
      }
    },

    disableHireDeliveryRequest() {
      if (this.hire?.delivery_job_id) {
        this.hdRequested = true;
      }
    },

    checkRiders(check) {
      for (let driver of this.hire?.drivers || []) {
        if (check(driver)) {
          return true;
        }
      }

      return false;
    },

    getIncidentTariff() {
      window.Ajax.get(`/incidents/${this.incident}?parts=tariff`)
        .then(response => {
          this.selectedTariff = response.data.tariff;
        })
        .catch(error => {
          this.requestHdIndicator = false;
          window.toastify.error(
            'Error',
            'Sorry, there was an issue getting the tariff'
          );
        });
    }
  },

  watch: {
    $route(to, from) {
      this.mount();
    },
    hire() {
      this.mount();
    },
    useVehicleFromFleet(value) {
      // if (value) {
      //   this.vehicleForm.registration = null;
      //   this.vehicleForm.transmission_type_id = null;
      //   this.vehicleForm.group_id = null;
      //   this.vehicleForm.make_id = null;
      //   this.vehicleForm.model = null;
      //   this.vehicleForm.colour = null;
      // } else {
      //   this.clearVehicle();
      // }
    },
    vehicleGroup(id) {
      if (!this.useVehicleFromFleet) {
        const group = this.groups.find(group => group.id == id);

        if (group) {
          this.setGTARate(group);
        }
      }
    },
    'hire.drivers'(newVal) {
      this.enableHireDeliveryRequest();
    }
  }
};
</script>
